import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, Outlet, Link } from "react-router-dom";
import {
  LogOut as LogOutIcon,
  User as UserIcon,
  Settings as SettingsIcon,
  LayoutDashboard as DashboardIcon,
  Gamepad2 as GamepadIcon,
  Coffee as CoffeeIcon,
  Eye as EyeIcon,
  InfoIcon, // 💡 NEW: Import the Eye icon for Spectate
} from "lucide-react";
import axios from "axios";
import "./ProtectedLayout.css";

import LoadingPage from "../../pages/LoadingPage/LoadingPage";
import { useAuth } from "../../context/AuthContext";

const USER_ME_URL = `${process.env.REACT_APP_API_BASE_URL}/api/user/me`;
const LOGOUT_URL = `${process.env.REACT_APP_API_BASE_URL}/api/logout`;

function ProtectedLayout() {
  const navigate = useNavigate();
  // 💡 Remove loading, isAuthenticated state, and redirection logic from here.
  // We only need the user data state.
  const [userData, setUserData] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // 💡 INTEGRATE useAuth: Use the global state and setter functions
  const {
    isAuthenticated, // Check this instead of refreshUserData() result
    loadingAuth, // Use this instead of local 'loading' state
    setIsAuthenticatedFalse, // Use this instead of local setUserData(null) and navigate
  } = useAuth();

  // 1. UPDATED handleLogout: Clears the session on the backend and updates global state
  const handleLogout = useCallback(async () => {
    try {
      // 1. Tell the backend to clear the HttpOnly cookie
      await axios.post(
        LOGOUT_URL,
        {},
        {
          withCredentials: true,
        }
      );
    } catch (error) {
      console.error("Logout network warning:", error);
      // NOTE: Even if network fails, we proceed to clear client state
    }

    // 2. CRITICAL: Update the global authentication state
    setIsAuthenticatedFalse();

    // 3. Navigate away (ProtectedLayout's logic will handle the redirect now)
    navigate("/", { replace: true });
  }, [navigate, setIsAuthenticatedFalse]);

  // 2. UPDATED refreshUserData: Now focuses *only* on fetching the user's profile data
  const refreshUserData = useCallback(async () => {
    try {
      const response = await axios.get(USER_ME_URL, {
        withCredentials: true,
      });

      // Axios throws for 4xx/5xx status codes, so if we reach here, it's a 200 OK.
      setUserData(response.data);
      return true; // Auth SUCCESS
    } catch (error) {
      // This catches the 401/403 from the backend
      console.error("User profile fetch failed, session invalid or expired.");

      // 💥 CRITICAL: If profile fetch fails, force global logout.
      // This will ensure the whole app knows the session is invalid.
      setIsAuthenticatedFalse();
      setUserData(null);
      // navigate("/") is now handled by the check below
      return false; // Auth FAIL
    }
  }, [setIsAuthenticatedFalse]);

  // 3. SIMPLIFIED Auth Check on Mount
  useLayoutEffect(() => {
    // Only fetch user data if the AuthContext says we *might* be logged in
    if (isAuthenticated) {
      refreshUserData();
    }
  }, [isAuthenticated, refreshUserData]); // Run when global auth status changes

  // 4. CRITICAL NAVIGATION EFFECT: If context says unauthenticated, redirect immediately.
  useEffect(() => {
    // If the check on mount fails (isAuthenticated is false)
    if (!loadingAuth && !isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [loadingAuth, isAuthenticated, navigate]);

  // --- Helper Functions (Updated) ---
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleMenuItemClick = (action) => {
    setIsMenuOpen(false);
    if (action === "logout") {
      handleLogout();
    } else if (action === "profile") {
      navigate("/profile");
    } else if (action === "dashboard") {
      navigate("/dashboard");
    } else if (action === "games") {
      navigate("/games");
      // 💡 NEW: Handler for spectate games
    } else if (action === "spectate") {
      navigate("/games/spectate-auth");
    } else if (action === "guide") {
      // Add this case
      navigate("/guide");
    }
  };

  const handleBuyCoffeeClick = () => {
    setIsMenuOpen(false);
    window.open("https://www.buymeacoffee.com/smacksmackk", "_blank");
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  const username = userData?.discord_username || "Player"; // Assuming 'discord_username'
  const avatarUrl = userData?.avatar_url;
  const initials = username.charAt(0).toUpperCase();

  const outletContextValue = useMemo(
    () => ({
      userData,
      refreshUserData,
      handleLogout,
    }),
    [userData, refreshUserData, handleLogout]
  );

  // --- GUARD CLAUSES ---

  // 1. GUARD CLAUSE: Use the global loading state
  if (loadingAuth || !userData) {
    // Show a loading page until global auth check is done AND user data is fetched
    return (
      <div
        className="full-screen-loader"
        style={{ backgroundColor: "#121212", minHeight: "100vh" }}
      >
        <LoadingPage
          message={
            loadingAuth
              ? "Checking authentication..."
              : "Loading user profile..."
          }
          isContentLoader={true}
        />
      </div>
    );
  }

  // NOTE: The redirecting logic is now handled by the useEffect(..., [isAuthenticated]) hook.

  // Full UI render for authenticated users.
  return (
    <div className="protected-layout">
      <nav className="navbar">
        <div className="navbar-brand">
          <Link to="/dashboard">OSRS Snakes & Ladders</Link>
        </div>

        <div className="navbar-links" ref={menuRef}>
          <button
            onClick={toggleMenu}
            className="avatar-menu-button"
            aria-expanded={isMenuOpen}
            aria-controls="profile-dropdown-menu"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={`${username}'s avatar`}
                className="user-avatar-large"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <span className="user-initials">{initials}</span>
            )}
          </button>

          {isMenuOpen && (
            <div id="profile-dropdown-menu" className="profile-dropdown">
              <div className="menu-header">
                <p className="menu-username">{username}</p>
              </div>

              <button
                className="menu-item"
                onClick={() => handleMenuItemClick("dashboard")}
              >
                <DashboardIcon size={18} className="menu-icon" />
                Dashboard
              </button>

              <button
                className="menu-item"
                onClick={() => handleMenuItemClick("profile")}
              >
                <UserIcon size={18} className="menu-icon" />
                Profile
              </button>

              <button
                className="menu-item"
                onClick={() => handleMenuItemClick("games")}
              >
                <GamepadIcon size={18} className="menu-icon" />
                My Games
              </button>

              {/* 💡 NEW: Spectate Games Option */}
              <button
                className="menu-item"
                onClick={() => handleMenuItemClick("spectate")}
              >
                <EyeIcon size={18} className="menu-icon" />
                Spectate Games
              </button>

              <button
                className="menu-item"
                onClick={() => handleMenuItemClick("guide")}
              >
                <InfoIcon size={18} className="menu-icon" />{" "}
                Platform Guide
              </button>

              <button
                className="menu-item buy-coffee-option"
                onClick={handleBuyCoffeeClick}
              >
                <CoffeeIcon size={18} className="menu-icon" />
                Buy Me a Coffee
              </button>

              <div className="menu-separator"></div>

              <button
                className="menu-item logout-option"
                onClick={() => handleMenuItemClick("logout")}
              >
                <LogOutIcon size={18} className="menu-icon" />
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>

      <main className="protected-content">
        <Outlet context={outletContextValue} />
      </main>
    </div>
  );
}

export default ProtectedLayout;
